import dayjs from 'dayjs'
import { forwardRef, useLayoutEffect, useRef, useState } from 'react'
import { commonUtils } from '@/helpers/commonUtils'
import styles from './ProjectSharePoster.module.less'
import cls from 'classnames'
import MiddleText from '@/components/MiddleText/MiddleText'
import { INTERNAL_HOST } from '@/commonConst'
import { Trans, useTranslation } from 'react-i18next'

interface Props {
  project: ProjectModels.ProjectDetail
  userName: string
  userAvatar?: string | null
  downloadedAt?: number
  percentile?: number | null
}

const ProjectSharePoster = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { t } = useTranslation()
  const isInternal = INTERNAL_HOST.includes(location.hostname)
  const adNameRef = useRef<HTMLParagraphElement>(null)
  const [displayAdName, setDisplayAdName] = useState(props.project.adName || '')
  const coverUrl = props.project.coverPath || commonUtils.ossVideoToPng(props.project.adUrl)
  const creatorName = props.userName || 'A'
  const timestampTime = dayjs(props.downloadedAt || Date.now())
  const offsetMinutes = timestampTime.utcOffset()
  const offsetSign = offsetMinutes >= 0 ? '+' : '-'
  const offsetAbsMinutes = Math.abs(offsetMinutes)
  const offsetHours = Math.floor(offsetAbsMinutes / 60)
  const offsetRemainMinutes = offsetAbsMinutes % 60
  const timezoneText =
    offsetRemainMinutes === 0
      ? `UTC${offsetSign}${offsetHours}`
      : `UTC${offsetSign}${offsetHours}:${String(offsetRemainMinutes).padStart(2, '0')}`
  const timestamp = timestampTime.format('YYYY-MM-DD HH:mm')
  const percentileText = props.percentile === null || props.percentile === undefined ? '--' : props.percentile

  useLayoutEffect(() => {
    const element = adNameRef.current
    const fullText = props.project.adName || ''
    if (!element) {
      setDisplayAdName(fullText)
      return
    }

    element.textContent = fullText
    if (element.scrollHeight <= element.clientHeight) {
      setDisplayAdName(fullText)
      return
    }

    let left = 0
    let right = fullText.length
    let best = '...'
    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      const candidate = `${fullText.slice(0, mid)}...`
      element.textContent = candidate
      if (element.scrollHeight <= element.clientHeight) {
        best = candidate
        left = mid + 1
      } else {
        right = mid - 1
      }
    }
    setDisplayAdName(best)
  }, [props.project.adName])

  return (
    <div className={cls(styles.projectSharePoster)} ref={ref}>
      <div className={styles.score}>
        <img src={commonUtils.getImagePath('projectDetailPage/share-aigc-poster-score-bg.png')} alt="" />
        <span>{props.project.globalMetrics.overall.score}</span>
      </div>
      <div className={styles.userPill}>
        <img className={styles.avatar} src={commonUtils.getImagePath('projectDetailPage/share-aigc-poster-default-avatar.png')} alt='' />
        <div className={styles.userName}>
          <MiddleText text={creatorName} />
        </div>
      </div>
      <div className={styles.header}>
        <p className={styles.subTitle}>{t('dashboard.projectDetailPage.sharePoster.createdByYou')}</p>
        <p ref={adNameRef} className={styles.projectName}>{displayAdName}</p>
        <img className={styles.nameBg} src={commonUtils.getImagePath('projectDetailPage/share-aigc-poster-name-bg.png')} alt="" />
      </div>
      <div className={styles.scoreBox}>
        <span className={styles.scoreRow}>
          <Trans
            i18nKey='dashboard.projectDetailPage.sharePoster.scoreTemplate'
            values={{ score: `${percentileText}%` }}
            components={{ score: <span className={styles.percentileText} /> }}
          />
        </span>
        <p className={styles.timeText}>
          {t('dashboard.projectDetailPage.sharePoster.statsAt')}{timestamp}
          <span className={styles.timezoneText}>{timezoneText}</span>
        </p>
      </div>
      <div className={styles.coverWrap}>
        <div className={styles.coverContainer}>
          <img className={styles.cover} src={coverUrl} alt='' crossOrigin='anonymous' />
          <div className={styles.playIconBox}>
            <img className={styles.playIcon} src={commonUtils.getImagePath('projectDetailPage/share-aigc-poster-play-icon.png')} alt="" />
          </div>
        </div>
        <div className={styles.certification}>
          <div className={styles.certificationText}>
            <img className={styles.certificationIconOne} src={commonUtils.getImagePath('projectDetailPage/share-aigc-poster-tag-1.png')} alt="" />
            <MiddleText className={styles.certificationTxt} text={t('dashboard.projectDetailPage.sharePoster.certification')} />
            <img className={styles.certificationIconTwo} src={commonUtils.getImagePath('projectDetailPage/share-aigc-poster-tag-2.png')} alt="" />
            <img className={styles.certificationIconThree} src={commonUtils.getImagePath('projectDetailPage/share-aigc-poster-tag-3.png')} alt="" />
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerContent}>
            <img className={styles.footerLogo} src={commonUtils.getImagePath('homePage/adeff-logo.png')} alt='' />
            <p className={styles.footerText}>{t('dashboard.projectDetailPage.sharePoster.footerText')}</p>
          </div>
          <img className={styles.qrcode} src={
            isInternal
              ? commonUtils.getImagePath('projectDetailPage/share-aigc-poster-cn-qrcode.png')
              : commonUtils.getImagePath('projectDetailPage/share-aigc-poster-com-qrcode.png')
          } alt="" />
        </div>
      </div>
      <div className={styles.hostname}>
        <img src={commonUtils.getImagePath('projectDetailPage/share-aigc-poster-internet.png')} alt="" />
        <span>{location.hostname}</span>
      </div>
    </div>
  )
})


export default ProjectSharePoster
